import { Shape, Vector2Like, Vector2Tuple, Vector3 } from 'three'
import { WallSegmentWithShapePoints } from '~/components/DrawingCanvas/lib/collisionMeshes'
import Offset from '~/components/DrawingCanvas/lib/offset'
import { WallSegment } from '~/store/objects/types'

const EPSILON = 0.05

// See: https://stackoverflow.com/questions/1165647/how-to-determine-if-a-list-of-polygon-points-are-in-clockwise-order
function signedArea(points: Vector2Tuple[]) {
  const startAndEndAreSame =
    points[0][0] - points[points.length - 1][0] < EPSILON && points[0][1] - points[points.length - 1][1] < EPSILON

  const polygon = startAndEndAreSame ? points : [...points, points[0]]
  const edgeCount = polygon.length - 1

  let sum = 0

  for (let i = 0; i < edgeCount; i += 1) {
    sum += (polygon[i + 1][0] - polygon[i][0]) * (polygon[i + 1][1] + polygon[i][1])
  }

  return sum
}

export function getCounterClockwisePoints(points: Vector2Like[]) {
  if (points.length > 2 && signedArea(points.map(({ x, y }) => [x, y])) > 0) {
    return points.slice().reverse()
  }

  return points
}

export function computeEdges(pts: Vector2Like[], thickness: number) {
  const points = getCounterClockwisePoints(pts).map<Vector2Tuple>(({ x, y }) => [x, y])
  const { insetEdges, outsetEdges } = Offset.offset(
    points,
    points.map(() => thickness)
  )
  const centerEdges: [Vector2Tuple, Vector2Tuple][] = []
  for (let i = 0; i < points.length - 1; i++) {
    centerEdges.push([
      [points[i][0], points[i][1]],
      [points[i + 1][0], points[i + 1][1]],
    ])
  }

  return { insetEdges, outsetEdges, centerEdges }
}

export const getSegmentShape = ({
  insetEdge,
  outsetEdge,
}: Pick<WallSegmentWithShapePoints, 'insetEdge' | 'outsetEdge'>) => {
  const { start: a, end: b } = insetEdge
  const { start: c, end: d } = outsetEdge
  const shape = new Shape().moveTo(a.x, a.y).lineTo(c.x, c.y).lineTo(d.x, d.y).lineTo(b.x, b.y).closePath()
  return shape
}

export const getPerimeterPoints = (segments: WallSegment[]): Vector3[] => {
  const perimeterPoints = segments.map(({ startPoint }) => new Vector3().copy(startPoint))
  const finalPoint = segments[segments.length - 1].endPoint
  perimeterPoints.push(new Vector3().copy(finalPoint))
  return perimeterPoints
}

export const getSegmentOffsets = (segments: WallSegment[]) => {
  const perimeterPoints: Vector2Tuple[] = segments.map(({ startPoint }) => [startPoint.x, startPoint.y])
  const finalPoint = segments[segments.length - 1].endPoint
  perimeterPoints.push([finalPoint.x, finalPoint.y])
  const { insetEdges, outsetEdges } = Offset.offset(
    perimeterPoints,
    segments.map(({ thickness }) => thickness)
  )
  return { insetEdges, outsetEdges }
}

export const getInteriorWallFullHeight = (segments: Record<string, WallSegment>) => {
  const minHeight = Object.values(segments).reduce(
    (prev, { height, layerKey }) => Math.min(prev, layerKey === 'EXTERIOR_WALLS' ? height : Infinity),
    Infinity
  )
  const ONE_FOOT_PADDING = 12
  return minHeight - ONE_FOOT_PADDING
}
